import React, { useState } from 'react'
import MaterialTable from '@material-table/core'
import { TableCell } from '@mui/material'
import * as UIcons from '@iconscout/react-unicons'
import { withTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import useSWR, { mutate } from 'swr'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import TableActionBtn from 'components/TableActionBtn'
import StatusPill from '../../shared/StatusPill'
import { showModal, previewModal } from 'store/slices/miscSlice'
import AddPOS from './AddPOS'
import EmptyPOS from './EmptyPOS'

dayjs.extend(relativeTime)

export default withTranslation()(function POSTable({ t, q = '', branch = '', deviceModel = '', status = '' }) {
  const dispatch = useDispatch()
  const { data } = useSWR('pos', { revalidateOnFocus: true })
  const { data: branchesRes } = useSWR('merchants/branches')
  const [editingDevice, setEditingDevice] = useState(null)
  const [selectedCount, setSelectedCount] = useState(0)

  const devices = data?.data ?? []
  const branches = branchesRes?.data ?? []
  const branchNameById = branches.reduce((acc, b) => ({ ...acc, [b.id]: b.name }), {})

  const filtered = devices.filter((device) => {
    const query = q.trim().toLowerCase()
    if (query && !`${device.name ?? ''} ${device.code ?? ''}`.toLowerCase().includes(query)) return false
    if (branch && `${device.branch_id}` !== `${branch}`) return false
    if (deviceModel && device.device_model !== deviceModel) return false
    if (status && (device.status ?? 'active') !== status) return false
    return true
  })

  const openDetail = (device) => dispatch(previewModal({
    title: t('pos.management.view_details'),
    content: () => (
      <ul>
        <li className="grid grid-cols-2 gap-2 p-3 px-5">
          <label className="text-secondary font-medium">{t('pos.management.table.header.pos_id')}</label>
          <span className="text-placeholder">{device.code}</span>
        </li>
        <li className="grid grid-cols-2 gap-2 p-3 px-5">
          <label className="text-secondary font-medium">{t('pos.management.table.header.branch')}</label>
          <span className="text-placeholder">{branchNameById[device.branch_id] ?? '—'}</span>
        </li>
        <li className="grid grid-cols-2 gap-2 p-3 px-5">
          <label className="text-secondary font-medium">{t('pos.management.table.header.assigned_operator')}</label>
          <span className="text-placeholder">{device.user ?? '—'}</span>
        </li>
        <li className="grid grid-cols-2 gap-2 p-3 px-5">
          <label className="text-secondary font-medium">{t('pos.management.table.header.device_model')}</label>
          <span className="text-placeholder">{device.device_model ?? '—'}</span>
        </li>
        <li className="grid grid-cols-2 gap-2 p-3 px-5">
          <label className="text-secondary font-medium">{t('misc.status')}</label>
          <span className="text-placeholder">{device.status ?? 'active'}</span>
        </li>
      </ul>
    )
  }))

  const openToggleStatus = (device) => {
    const willDisable = device.status !== 'inactive'
    dispatch(showModal({
      title: t(willDisable ? 'pos.management.disable_title' : 'pos.management.enable_title'),
      content: () => (
        <div className="text-md w-full text-center">
          {t(willDisable ? 'pos.management.disable_confirm' : 'pos.management.enable_confirm')}
        </div>
      ),
      url: `pos/${device.id}`,
      method: 'put',
      extraPayload: { status: willDisable ? 'inactive' : 'active' },
      btnText: t(willDisable ? 'pos.management.disable' : 'pos.management.enable'),
      disableBtn: false,
      danger: willDisable,
      mutate: 'pos',
      successMsg: t(willDisable ? 'pos.management.success_disable' : 'pos.management.success_enable')
    }))
  }

  const openDelete = (device) => dispatch(showModal({
    title: t('pos.management.delete_title'),
    content: () => (
      <div className="text-md w-full text-center">
        {t('pos.management.delete_confirm')}
      </div>
    ),
    url: `pos/${device.id}`,
    method: 'delete',
    btnText: t('pos.management.delete'),
    disableBtn: false,
    danger: true,
    mutate: 'pos',
    successMsg: t('pos.management.success_delete')
  }))

  if (!devices.length) return <EmptyPOS />

  return (
    <div className="w-full bg-white rounded-xl border border-border-lines overflow-hidden">
      <MaterialTable
        columns={[
          { title: t('pos.management.table.header.pos_id'), field: 'code' },
          { title: t('pos.management.table.header.branch'), field: 'branch_id', render: (row) => branchNameById[row.branch_id] ?? '—' },
          { title: t('pos.management.table.header.assigned_operator'), field: 'user', render: (row) => row.user ?? '—' },
          { title: t('pos.management.table.header.device_model'), field: 'device_model', render: (row) => row.device_model ?? '—' },
          {
            title: t('pos.management.table.header.last_sync'),
            field: 'last_sync',
            render: (row) => row.last_sync ? dayjs(row.last_sync).fromNow() : t('pos.management.table.never_synced')
          },
          { title: t('pos.management.table.header.status'), field: 'status', render: (row) => <StatusPill active={(row.status ?? 'active') === 'active'} /> }
        ]}
        data={filtered}
        actions={[{ position: 'row', tooltip: t('pos.management.view_details') }]}
        onSelectionChange={(rows) => setSelectedCount(rows.length)}
        options={{
          selection: true,
          actionsColumnIndex: -1,
          toolbar: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          headerStyle: { textTransform: 'uppercase', fontSize: '12px', color: '#748799', backgroundColor: '#F5F6FA' }
        }}
        localization={{
          header: { actions: '' },
          body: { emptyDataSourceMessage: t('misc.no_records') }
        }}
        components={{
          Pagination: (props) => {
            const totalPages = Math.max(1, Math.ceil(props.count / props.rowsPerPage))
            const currentPage = props.page + 1

            return (
              <TableCell colSpan={props.colSpan}>
                <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                  <span className="text-sm text-secondary">
                    {t('pos.management.table.selected', { count: selectedCount, total: props.count })}
                  </span>

                  <div className="flex items-center gap-4">
                    <select
                      value={props.rowsPerPage}
                      onChange={(event) => props.onRowsPerPageChange(event)}
                      className="border border-border-lines rounded-lg px-2 py-1 text-sm text-dark-blue"
                    >
                      {(props.rowsPerPageOptions ?? [10, 20, 50]).map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>

                    <span className="text-sm text-secondary whitespace-nowrap">
                      {t('pos.management.table.page_of', { page: currentPage, total: totalPages })}
                    </span>

                    <div className="flex items-center gap-1">
                      <button type="button" onClick={(event) => props.onPageChange(event, 0)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page - 1)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page + 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleRight size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, totalPages - 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleRight size={16} className="text-secondary" />
                      </button>
                    </div>
                  </div>
                </div>
              </TableCell>
            )
          },

          Action: ({ data: row }) => (
            <TableActionBtn
              data={[
                {
                  Content: { icon: UIcons.UilInfoCircle, text: t('pos.management.view_details') },
                  action: () => openDetail(row)
                },
                {
                  Content: { icon: UIcons.UilEditAlt, text: t('pos.management.edit') },
                  action: () => setEditingDevice(row)
                },
                {
                  Content: { icon: UIcons.UilBan, text: t(row.status !== 'inactive' ? 'pos.management.disable' : 'pos.management.enable') },
                  action: () => openToggleStatus(row)
                },
                {
                  Content: { icon: UIcons.UilTrash, text: t('pos.management.delete'), classList: 'text-failure' },
                  action: () => openDelete(row)
                }
              ]}
            />
          )
        }}
      />

      {
        editingDevice && (
          <AddPOS
            device={editingDevice}
            handleClick={() => setEditingDevice(null)}
            onSaved={() => mutate('pos')}
          />
        )
      }
    </div>
  )
})
